import {
  Badge,
  Body1,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Spinner,
  Tab,
  TabList,
  Text,
  Title3,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  LONG_SHIFT_THRESHOLD_HOURS,
  payrollPeriodFor,
  payrollPeriodWindow,
  shiftPayrollPeriod,
  todayInZone,
} from '@worklogix/shared';
import type {
  CallerLink,
  Department,
  Employee,
  EmployeeSupervisorMapping,
  ListSessionsScope,
  PayrollPeriod,
  PunchPhotoMeta,
  SessionPhotosResponse,
  TimeSession,
} from '@worklogix/shared';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { MOBILE, SHRINKABLE } from '../responsive';

import { useTimeAdminApi } from './api';
import type {
  PatchSessionInput,
  PayrollRunPreview,
  PostResultFilter,
  SessionFilters,
  TimeSupervisor,
} from './api';
import { CenterModal } from './components/CenterModal.js';
import type { DataColumn } from './components/DataTable';
import { DateTimeField } from './components/DateTimeField.js';
import { WeekCalendar } from './components/WeekCalendar.js';
import { toErrorMessage } from './errors';
import { deriveSessionFlags, hasAnyFlag } from './flags';
import type { SessionFlagKind } from './flags';
import {
  elapsedHours,
  formatClock,
  formatDay,
  formatDayShort,
  fromLocalInputValue,
  toLocalInputValue,
} from './format';
import { NewTimeEntryDialog } from './NewTimeEntryDialog';
import { photoBadgeKind, sessionShowsPhoto } from './photos/photoUrl';
import { MissingPhotoBadge, PhotoEnlargeDialog, PunchThumbnail } from './photos/PunchPhoto';
import { indexPostResults, postOutcomeBadge } from './postOutcome';
import type { PostOutcomeBadge } from './postOutcome';
import { MyReportsTab } from './reports/MyReportsTab';
import {
  approvedViaBadge,
  canApproveInUi,
  effectiveScope,
  initialScope,
  needsAdminEmployeeIds,
  NOT_LINKED_BANNER,
  shouldShowNotLinkedBanner,
} from './supervisorApproval';
import {
  computeRowLayout,
  groupSessionsByEmployee,
  orderSessionsByEmployee,
} from './timecardGrouping';
import { useIsAdmin } from './useIsAdmin';
import { useIsViewer } from './useIsViewer';
import { useLiveness } from './useLiveness';

const useStyles = makeStyles({
  page: {
    ...SHRINKABLE,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    // Reserve room for title + one subtitle line so the header — and the tab row
    // below it — never shift vertically when switching tabs (some tabs have no
    // session-count subtitle).
    minHeight: '3.25rem',
  },
  // Always occupies one line's height even when empty, keeping the header stable.
  subtitle: {
    minHeight: tokens.lineHeightBase300,
  },
  filters: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  flagCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
  photoCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'center',
  },
  photoRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  photoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    alignItems: 'flex-start',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
    flexWrap: 'wrap',
  },
  /**
   * "Entry 2 of 3 · Approved", with an arrow either side. Centred, because it is the
   * dialog's orientation line and a reader paging through a split day should not have
   * to re-find it each time the entry underneath it changes shape.
   */
  entryPager: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
  },
  /** The note under an accepted flag: who waved it through, and when. */
  ackNote: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  banner: {
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  warningBanner: {
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorPaletteYellowBackground2,
  },
  deptCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  selectCell: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  /**
   * Columns that carry NOTHING a phone reader needs at a glance.
   *
   * A ten-column timecard on a 390px screen is unreadable — the employee's name
   * scrolls off the left before you reach the hours (Michael, 2026-09-10). These
   * drop out below the breakpoint: Department repeats what the group header
   * already totals, and Photo / Approval / Post result are all reachable by
   * tapping the row. Nothing is lost, it moves one tap away.
   */
  hideOnMobile: {
    [MOBILE]: {
      display: 'none',
    },
  },
  /** Long form: desktop only. */
  wideOnly: {
    [MOBILE]: { display: 'none' },
  },
  /** Short form: phone only. Paired with wideOnly so the switch is pure CSS. */
  narrowOnly: {
    display: 'none',
    [MOBILE]: { display: 'inline' },
  },
  /** Long-shift detail, only once asked for. */
  longShiftNames: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    paddingTop: tokens.spacingVerticalS,
  },
  /** The banner entries read as links, not as a column of buttons. */
  flagSummary: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
    marginTop: tokens.spacingVerticalXS,
  },
  longShiftLink: {
    paddingLeft: 0,
    paddingRight: 0,
    minWidth: 'auto',
    textDecorationLine: 'underline',
  },
  longShiftNote: { color: tokens.colorNeutralForeground3 },
  // A tab strip must never widen the page; swipe it instead. See AdminTab.
  tabStrip: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    ...SHRINKABLE,
  },
  bulkBar: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  /**
   * The page-level admin actions, stacked. "Review payroll corrections" sits UNDER
   * "Post approved to NetSuite" (Michael, 2026-09-14) because the two are the same
   * kind of thing — a whole-payroll action — and he wanted it readily available
   * rather than buried on another screen.
   */
  adminActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    alignItems: 'stretch',
  },
  /** Preview warnings. Coloured, because every one of them is one-way or manual. */
  warningText: {
    color: tokens.colorStatusWarningForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  // --- Bespoke employee-grouped timecard table ---
  /**
   * PHONE LAYOUT for the timecard: one card per shift, all fields stacked.
   *
   * Six columns of times, hours, status and flag badges do not fit 390px. Hiding
   * columns and shortening text bought ~80px and it STILL did not fit — DAY sat
   * off the left edge and the flag badges were cut off the right (Michael,
   * 2026-09-10). Stacking is the only layout that shows every field without
   * clipping, and it shows MORE than the table did: department, photos and
   * approval come back, because vertical space is cheap.
   *
   * Reuses the same `columns` definitions as the table, so every value is
   * formatted in exactly one place.
   */
  mobileBlocks: {
    display: 'none',
    [MOBILE]: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalL,
      ...SHRINKABLE,
    },
  },
  mBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    ...SHRINKABLE,
  },
  mBlockName: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
  },
  mBlockTotals: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  mCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    ...SHRINKABLE,
  },
  mField: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalM,
    ...SHRINKABLE,
  },
  mLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    flexShrink: 0,
  },
  mValue: {
    textAlign: 'right',
    overflowWrap: 'anywhere',
    ...SHRINKABLE,
  },
  tableWrap: {
    // Replaced by `mobileBlocks` below the breakpoint.
    [MOBILE]: { display: 'none' },
    // A data table stays TABULAR on a phone and scrolls sideways INSIDE this box —
    // columns like date/hours/status are only meaningful next to each other. The
    // SHRINKABLE part is what makes that work: without minWidth:0 this box refuses to
    // become narrower than the table and the page scrolls instead.
    ...SHRINKABLE,
    WebkitOverflowScrolling: 'touch',
    overflowX: 'auto',
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: tokens.fontSizeBase300,
  },
  headCell: {
    [MOBILE]: {
      paddingLeft: tokens.spacingHorizontalXS,
      paddingRight: tokens.spacingHorizontalXS,
      fontSize: tokens.fontSizeBase200,
    },
    textAlign: 'left',
    paddingTop: tokens.spacingVerticalSNudge,
    paddingBottom: tokens.spacingVerticalSNudge,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke3,
    whiteSpace: 'nowrap',
  },
  cell: {
    [MOBILE]: {
      paddingLeft: tokens.spacingHorizontalXS,
      paddingRight: tokens.spacingHorizontalXS,
      fontSize: tokens.fontSizeBase200,
      // Rows were roughly a finger-and-a-half tall, so three shifts filled the
      // screen. Vertical padding is what made them that tall, not the content.
      paddingTop: tokens.spacingVerticalXS,
      paddingBottom: tokens.spacingVerticalXS,
      // Five columns of times and hours must not wrap mid-value.
      whiteSpace: 'nowrap',
    },
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke3,
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },
  dataRow: {
    cursor: 'pointer',
    '&:hover td': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  // Alternating date bands (#4) — a subtle hue shift each time the day changes.
  bandA: {
    '& td': { backgroundColor: tokens.colorNeutralBackground1 },
  },
  bandB: {
    '& td': { backgroundColor: tokens.colorNeutralBackground2 },
  },
  // Continuous-chain left accent (#3), applied to the first cell so a chain reads
  // as one bracketed group; colors cycle so adjacent chains stay distinguishable.
  chain0: {
    '& td:first-child': { boxShadow: `inset 3px 0 0 0 ${tokens.colorBrandStroke1}` },
  },
  chain1: {
    '& td:first-child': { boxShadow: `inset 3px 0 0 0 ${tokens.colorPaletteMarigoldBorderActive}` },
  },
  chain2: {
    '& td:first-child': { boxShadow: `inset 3px 0 0 0 ${tokens.colorPaletteTealBorderActive}` },
  },
  chain3: {
    '& td:first-child': { boxShadow: `inset 3px 0 0 0 ${tokens.colorPalettePurpleBorderActive}` },
  },
  // Gap between distinct punch-groups (#3) — clean whitespace, not a filled bar.
  gapCell: {
    height: tokens.spacingVerticalM,
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  // Employee "at a glance" block header (#2) — a soft banner, not a heavy bar.
  glanceCell: {
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground2,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke3,
  },
  glanceHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  glanceName: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
  },
  // The totals line under the name — clean text, no pills.
  glanceTotalsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    [MOBILE]: {
      // One line, not a wrapped paragraph of per-department splits.
      columnGap: tokens.spacingHorizontalS,
    },
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalXXS,
    alignItems: 'baseline',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  /** Per-department split — the group Total carries it on a phone. */
  glanceDeptOnMobile: {
    [MOBILE]: { display: 'none' },
  },
  glanceDeptGroup: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalXS,
  },
  glanceDeptName: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  // Pending work — the actionable number, amber + emphasized.
  glancePending: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorStatusWarningForeground1,
  },
  // Already approved — recessed so pending stands out.
  glanceDone: {
    color: tokens.colorNeutralForeground3,
  },
  glanceSep: {
    color: tokens.colorNeutralForeground4,
  },
  glanceTotalGroup: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalXS,
    paddingLeft: tokens.spacingHorizontalL,
    borderLeftWidth: '1px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke2,
  },
  glanceTotalLabel: {
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
  },
  glanceMuted: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  emptyCell: {
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  approvalCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
  },
});

const ALL = '';

/**
 * Timecard columns dropped below the mobile breakpoint.
 *
 * `department` is already totalled per department in each employee's group
 * header; `photo`, `approval` and `postResult` are all reachable by tapping the
 * row open. Keeping Day / In / Out / Hours / Status is what makes a shift
 * readable on a phone without scrolling sideways past the name.
 */
const MOBILE_HIDDEN_COLUMNS: ReadonlySet<string> = new Set([
  'department',
  'photo',
  'approval',
  'postResult',
]);

/**
 * Picker label for a payroll period: "Current", "Previous", then the plain date
 * range. The range is always shown, including on Current/Previous — the whole
 * point of the control is to say WHICH dates are on screen, and a bare
 * "Current" leaves the reader counting back from today to work it out.
 */
function payrollPeriodOptionLabel(offset: number, period: PayrollPeriod): string {
  const range = `${period.startDate} – ${period.endDate}`;
  if (offset === 0) {
    return `Current · ${range}`;
  }
  if (offset === -1) {
    return `Previous · ${range}`;
  }
  return range;
}

/**
 * Top-level surface tabs. `timecards` is the timecard surface — every session in
 * the selected pay period, posted and unposted alike; `reports` is the My Reports
 * surface (PTO, bonuses, travel time…), rendered by {@link MyReportsTab}.
 *
 * A third `history` tab (Admin-only, posted entries) was REMOVED 2026-09-10: it
 * existed only to keep old posted rows out of the approval queue, and the
 * pay-period filter does that for every row at once.
 */
type TimecardTab = 'timecards' | 'reports';

/** Map a session's flag severity to a Fluent Badge color. */
function badgeColor(
  severity: 'danger' | 'warning' | 'important'
): 'danger' | 'warning' | 'important' {
  return severity;
}

/** Human status for a session (posted > approved > open/closed). */
/**
 * Compact status for a phone: 'Unapproved' alone is ~90px, which is what pushed
 * the STATUS column off the right edge (Michael, 2026-09-10). Same information,
 * fewer characters.
 */
function shortStatusLabel(s: TimeSession): string {
  if (s.postedToNs) {
    return 'Posted';
  }
  if (s.approvedAt !== undefined && s.approvedAt !== '') {
    return 'Appr.';
  }
  return s.clockOutAt === undefined ? 'Open' : 'Unappr.';
}

function statusLabel(s: TimeSession): string {
  if (s.postedToNs) {
    return s.nsTimebillId !== undefined ? `Posted (${s.nsTimebillId})` : 'Posted';
  }
  if (s.approvedAt !== undefined && s.approvedAt !== '') {
    return 'Approved';
  }
  return s.clockOutAt === undefined ? 'Open' : 'Unapproved';
}

/**
 * Hours & Timesheet — the supervisor review surface. Lists/filters time
 * sessions, opens a blade to inline-correct clock-in/out and approve, and posts
 * approved+unposted sessions to NetSuite (grouped server-side). Rows carrying
 * `needs_review` / clock-skew / frozen-conflict annotations are visibly badged.
 */
/**
 * The post-result filter's options, in the order a supervisor cares about them:
 * the problems first, the successes last.
 */
/**
 * The flag summary's chips: worst first, so the row reads in the order a supervisor
 * should work through it. `frozen` is deliberately absent — it is a state, not a
 * problem, and every posted session carries it.
 */
const FLAG_SUMMARY_ORDER = [
  'noEarning',
  'overlap',
  'longShift',
  'backToBack',
  'needsReview',
] as const satisfies readonly SessionFlagKind[];

const FLAG_SUMMARY_LABELS: Record<(typeof FLAG_SUMMARY_ORDER)[number], string> = {
  noEarning: 'No earning type',
  overlap: 'Overlapping',
  longShift: 'Long shift',
  backToBack: 'Back-to-back',
  needsReview: 'Needs review',
};

const POST_RESULT_LABELS = {
  // Keyed by ALL (the empty string this page already uses for "no filter"), so the
  // dropdown's value and the filter state are the same thing.
  '': 'All post results',
  failed: 'Failed to post',
  skipped: 'Skipped',
  not_attempted: 'Not attempted',
  posted: 'Posted',
} as const;

export function HoursTimesheet(): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();
  const isAdmin = useIsAdmin();
  const isViewer = useIsViewer();
  /** "New time entry" dialog — a supervisor recording a forgotten clock-in. */
  const [newEntryOpen, setNewEntryOpen] = useState(false);

  // No from/to here: the pay-period picker is the ONLY date control now (the
  // From/To inputs were removed 2026-09-10), and it supplies instants directly.
  const [filters, setFilters] = useState<SessionFilters>({
    departmentId: ALL,
    employeeId: ALL,
  });
  // Report-set scope. A non-admin never sends a scope — the server always
  // constrains them to their reports; an Admin defaults to the whole tenant.
  // (The old mine/all radio was replaced by the surface tabs; direct-report
  // scoping returns via the admin supervisor picker in a later step.)
  const [scope] = useState<ListSessionsScope>(() => initialScope(isAdmin, isViewer) ?? 'all');
  /**
   * Which payroll period the grid shows, as an offset from the current one
   * (0 = current, -1 = last week…). `null` means "All dates" — the previous
   * behaviour, where the From/To inputs drive the range.
   *
   * Defaults to the CURRENT period (Michael, 2026-09-10): the grid used to load
   * every unposted session ever recorded, so it grew without bound and stale rows
   * crowded out the week people were actually working on.
   */
  const [periodOffset, setPeriodOffset] = useState<number | null>(0);
  // Long-shift names are collapsed by default — the count is the alert.
  const [longShiftsOpen, setLongShiftsOpen] = useState(false);
  // Anchored once per mount, in the PAYROLL timezone — not the viewer's, or
  // someone opening this late on a Tuesday evening from another zone would land
  // on next week's empty period.
  const currentPeriod = useMemo<PayrollPeriod>(
    () => payrollPeriodFor(todayInZone(new Date())),
    []
  );
  const selectedPeriod = useMemo<PayrollPeriod | null>(
    () => (periodOffset === null ? null : shiftPayrollPeriod(currentPeriod, periodOffset)),
    [currentPeriod, periodOffset]
  );
  /**
   * The period as INSTANTS, not bare dates: `to` is an EXCLUSIVE bound on a
   * timestamptz, so sending the period's last day would silently drop that whole
   * Tuesday, and a bare date means midnight UTC — 7pm the previous evening in
   * Central. See payroll-window.ts for both traps.
   */
  const periodWindow = useMemo(
    () => (selectedPeriod === null ? null : payrollPeriodWindow(selectedPeriod)),
    [selectedPeriod]
  );
  // The current period plus the previous eight — far enough back for corrections
  // without turning the picker into a scroll.
  const periodChoices = useMemo(() => Array.from({ length: 9 }, (_, i) => -i), []);
  const [activeTab, setActiveTab] = useState<TimecardTab>('timecards');
  /**
   * Calendar or list.
   *
   * THE CALENDAR IS THE DEFAULT as of 2026-09-18. THE LIST STAYS.
   *
   * 🔥 IT IS NO LONGER SCHEDULED FOR DELETION. Michael, 2026-09-23: "Don't delete the
   * list view just yet. I have notes that need the list view for later." The earlier
   * plan to remove it after 2026-09-25 is CANCELLED — do not act on it, and do not put
   * a new date on it without asking him first.
   *
   * 🔥 AND IF IT IS EVER REMOVED, RE-CHECK WHAT IT CARRIES ALONE FIRST. The
   * audit that cleared this flip found four things it had and the calendar did not
   * (department, the full flag set, post result, and any phone layout at all); those
   * are closed. Photos turned out NOT to be a gap — the edit modal shows both punch
   * photos and every calendar card opens it. Anything added to the list between now
   * and the deletion needs the same check, or removing it quietly drops a capability.
   */
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('calendar');
  // Admin-only supervisor picker (Gate-2 decision #2). Empty for non-admins (the
  // endpoint is Admin-only). Picking one scopes the list to that supervisor's
  // direct reports via `filters.supervisorId`.
  const [supervisors, setSupervisors] = useState<TimeSupervisor[]>([]);
  // Bulk-approve selection: the set of session ids the reviewer has ticked. Only
  // ever holds approvable rows (the checkbox is offered on those only).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);
  const [sessions, setSessions] = useState<TimeSession[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  /**
   * People currently past the long-shift threshold on the clock, swept SEPARATELY from
   * the table and deliberately WITHOUT the date filters (Michael, 2026-09-03).
   *
   * The table is windowed on clock-in, so a forgotten clock-out from last night falls
   * outside "today" and a supervisor reviewing today's card would never see it. This
   * query asks the server for them across every date, which is what makes the flag
   * something that gets RAISED rather than something you have to go and find.
   */
  const [openLongShifts, setOpenLongShifts] = useState<TimeSession[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  // The caller's resolved employee link (drives the not-linked banner + whether
  // approve is offered). Null until loaded / on a load failure (fail-safe: no
  // banner, but approve still gated by role).
  const [link, setLink] = useState<CallerLink | null>(null);
  // employeeIds needing an Admin override (no usable supervisor / cycle). Only
  // populated for an Admin — the mapping endpoint is Admin-only.
  const [needsAdminIds, setNeedsAdminIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Blade / edit state.
  const [selected, setSelected] = useState<TimeSession | null>(null);
  /**
   * Every entry on the CARD the dialog was opened from, in the order the card drew
   * them — so the dialog can page between them.
   *
   * 🔥 Michael, 2026-09-30: "when someone clicks on a card that has multiple entries,
   * we should have each entry modal look the same. I want to add arrows though that
   * can switch back and forth between the entries and update the header status to show
   * 'Entry 1', 'Entry 2' ... This is also how we know which entry each flag is for."
   *
   * IT IS A SNAPSHOT OF THE CARD, not a fresh query. The card's numbering is what the
   * flag tooltips on it refer to, so paging has to walk the same list in the same
   * order; re-deriving the day here could disagree with what is still on screen behind
   * the dialog, and then "Entry 2" would mean two different entries at once.
   *
   * Opening from the LIST leaves it as a single-element list, and the arrows hide.
   */
  const [dayEntries, setDayEntries] = useState<readonly TimeSession[]>([]);
  /**
   * Department being edited (Michael, 2026-09-17: "We need admin to be able to update
   * the department that time entries are for. We had a lot of employees who logged in
   * under the wrong kiosk this week").
   *
   * 🔥 IT IS NOT COSMETIC. Department picks the earning type a TimeBill posts under —
   * resolveEarning matches (entry kind + department) against the employee's own
   * earnings — so a shift clocked at the wrong kiosk pays under the wrong line, or
   * fails to resolve an earning at all and is skipped.
   */
  const [editDept, setEditDept] = useState('');
  const [editIn, setEditIn] = useState('');
  const [editOut, setEditOut] = useState('');
  const [saving, setSaving] = useState(false);
  const [bladeError, setBladeError] = useState<string | null>(null);
  // Admin-only delete (two-step confirm) for test / erroneous entries.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Force clock-out dialog (admin + supervisors) for an OPEN session someone
  // forgot to close. Writes a clock-out via the same authorized edit path.
  const [clockOutTarget, setClockOutTarget] = useState<TimeSession | null>(null);
  const [clockOutValue, setClockOutValue] = useState('');
  const [clockOutSaving, setClockOutSaving] = useState(false);
  const [clockOutError, setClockOutError] = useState<string | null>(null);

  // Photo state (Phase 2). Metadata is fetched lazily when a kiosk session's
  // blade opens (never N calls on list render); the enlarge blade re-fetches the
  // full image through the RLS proxy on demand.
  const [photoMeta, setPhotoMeta] = useState<SessionPhotosResponse | null>(null);
  const [enlarged, setEnlarged] = useState<{ punchId: string; role: 'open' | 'close' } | null>(
    null
  );

  // Post-to-NetSuite state.
  const [posting, setPosting] = useState(false);
  const [postMessage, setPostMessage] = useState<string | null>(null);
  const [postError, setPostError] = useState<string | null>(null);
  // Per-session outcomes from the LAST post run (sessionId → badge). Persists
  // across the post's reload so `skipped`/`failed` sessions stay visibly flagged
  // (posted ones also freeze via `postedToNs`); keyed by id so stale entries
  // simply don't match any visible row.
  const [postOutcomes, setPostOutcomes] = useState<Map<string, PostOutcomeBadge>>(new Map());

  const employeesById = useMemo(() => {
    const m = new Map<string, Employee>();
    for (const e of employees) {
      m.set(e.id, e);
    }
    return m;
  }, [employees]);

  const departmentsById = useMemo(() => {
    const m = new Map<string, Department>();
    for (const d of departments) {
      m.set(d.id, d);
    }
    return m;
  }, [departments]);

  /**
   * Department NAMES only, for the calendar's cards.
   *
   * Derived from `departmentsById` rather than fetched again — the directory is
   * already loaded once for the filter pickers, and a second source for the same
   * names is a second thing to keep in step.
   */
  const departmentNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const [id, d] of departmentsById) {
      m.set(id, d.name);
    }
    return m;
  }, [departmentsById]);

  // Load directory once for name maps + filter pickers.
  useEffect(() => {
    let active = true;
    Promise.all([api.listEmployees(), api.listDepartments()])
      .then(([emps, depts]) => {
        if (active) {
          setEmployees(emps);
          setDepartments(depts);
        }
      })
      .catch(() => {
        // Directory is a convenience for labels/filters; a failure here is
        // non-fatal — sessions still load and fall back to raw ids.
      });
    return () => {
      active = false;
    };
  }, [api]);

  // Resolve the caller's own employee link (for the not-linked banner + approve
  // gating). Non-fatal on failure — leave null.
  useEffect(() => {
    let active = true;
    void api
      .getMyLink()
      .then((l) => {
        if (active) {
          setLink(l);
        }
      })
      .catch(() => {
        if (active) {
          setLink(null);
        }
      });
    return () => {
      active = false;
    };
  }, [api]);

  // Admin-only: pull the employee↔supervisor mapping so we can badge sessions
  // whose employee needs an Admin override. Skipped for non-admins (endpoint is
  // Admin-only and would 403); non-fatal on failure.
  useEffect(() => {
    if (!isAdmin) {
      setNeedsAdminIds(new Set());
      return;
    }
    let active = true;
    void api
      .listEmployeeMappings()
      .then((mappings: EmployeeSupervisorMapping[]) => {
        if (active) {
          setNeedsAdminIds(needsAdminEmployeeIds(mappings));
        }
      })
      .catch(() => {
        if (active) {
          setNeedsAdminIds(new Set());
        }
      });
    return () => {
      active = false;
    };
  }, [api, isAdmin]);

  // Admin-only: load the tenant's supervisors for the picker. Skipped for
  // non-admins (endpoint is Admin-only); non-fatal on failure (no picker).
  useEffect(() => {
    if (!isAdmin) {
      setSupervisors([]);
      return;
    }
    let active = true;
    void api
      .listSupervisors()
      .then((rows) => {
        if (active) {
          setSupervisors(rows);
        }
      })
      .catch(() => {
        if (active) {
          setSupervisors([]);
        }
      });
    return () => {
      active = false;
    };
  }, [api, isAdmin]);

  const reload = useCallback(() => {
    let active = true;
    setLoading(true);
    setError(null);
    const sc = effectiveScope(isAdmin, isViewer, scope);
    // The pay period supplies the date range; "All dates" supplies none.
    const dated: SessionFilters =
      periodWindow === null
        ? filters
        : { ...filters, from: periodWindow.from, to: periodWindow.to };
    const query: SessionFilters = sc === undefined ? dated : { ...dated, scope: sc };
    void api
      .listSessions(query)
      .then((rows) => {
        if (active) {
          setSessions(rows);
        }
      })
      .catch((err: unknown) => {
        if (active) {
          setError(toErrorMessage(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [api, filters, isAdmin, isViewer, periodWindow, scope]);

  useEffect(() => reload(), [reload]);

  /**
   * A BACKGROUND refresh — same rows, no spinner, no error banner.
   *
   * 🔥 IT CANNOT REUSE `reload`. That one sets `loading`, which blanks the whole grid
   * behind a spinner; running it every time a colleague approves something would make
   * the page flash all day and would be worse than having no refresh at all. A
   * background refresh has to be invisible unless the DATA changed.
   *
   * Errors are swallowed for the same reason the poll swallows its own: the next
   * change re-triggers this, and a banner over a self-healing blip trains people to
   * ignore banners.
   */
  // Only the entries actually on screen: presence for a session nobody is looking at
  // is a row fetched for nothing, every fifteen seconds.
  const visibleSessionIds = useMemo(() => sessions.map((s) => s.id), [sessions]);

  const quietRefresh = useCallback(() => {
    const sc = effectiveScope(isAdmin, isViewer, scope);
    const dated: SessionFilters =
      periodWindow === null
        ? filters
        : { ...filters, from: periodWindow.from, to: periodWindow.to };
    const query: SessionFilters = sc === undefined ? dated : { ...dated, scope: sc };
    void api
      .listSessions(query)
      .then((rows) => {
        setSessions(rows);
      })
      .catch(() => undefined);
  }, [api, filters, isAdmin, isViewer, periodWindow, scope]);

  /*
   * Cross-device refresh and the presence indicator (#1, #2 + 2026-09-19).
   *
   * `editing` is the entry in the open modal — presence is about who is WORKING on an
   * entry, not who can see it, so viewing the week registers nothing.
   */
  const { presenceBySession, pendingChange, applyPending } = useLiveness({
    from: periodWindow?.from ?? null,
    to: periodWindow?.to ?? null,
    editing: selected?.id ?? null,
    watching: visibleSessionIds,
    onChanged: quietRefresh,
    enabled: !loading && periodWindow !== null,
  });

  // The long-shift sweep. Shares `reload`'s scope so it can never show a manager
  // somebody outside their reports, but drops `from`/`to` on purpose. A failure here is
  // swallowed to an empty banner: this is an extra warning layered on the page, and it
  // must never replace the timesheet with an error.
  useEffect(() => {
    let active = true;
    const sc = effectiveScope(isAdmin, isViewer, scope);
    const query: SessionFilters = { status: 'long_shift', ...(sc === undefined ? {} : { scope: sc }) };
    void api
      .listSessions(query)
      .then((rows) => {
        if (active) {
          // Only the ones STILL on the clock. A closed 12-hour shift is a payroll-review
          // note and already carries its badge in the table; putting it up here too would
          // make the banner permanent, and a permanent banner is furniture.
          setOpenLongShifts(rows.filter((r) => r.longShift?.stillClockedIn === true));
        }
      })
      .catch(() => {
        if (active) {
          setOpenLongShifts([]);
        }
      });
    return () => {
      active = false;
    };
  // `sessions` is in the deps on purpose: it changes after a clock-out, an inline
  // correction or a delete, and each of those can clear a long shift. Re-sweeping then
  // keeps the banner from outliving the problem it names.
  }, [api, isAdmin, isViewer, scope, sessions]);

  // Lazily load punch-photo metadata for the OPEN blade (kiosk sessions only).
  // Manual sessions have no punch photos, so we skip the call entirely.
  useEffect(() => {
    if (selected === null || !sessionShowsPhoto(selected)) {
      setPhotoMeta(null);
      return;
    }
    let active = true;
    setPhotoMeta(null);
    void api
      .listSessionPhotos(selected.id)
      .then((meta) => {
        if (active) {
          setPhotoMeta(meta);
        }
      })
      .catch(() => {
        // Photo metadata is a review convenience; a failure here must not break
        // the edit/approve flow. Leave it null (no badges) and carry on.
        if (active) {
          setPhotoMeta(null);
        }
      });
    return () => {
      active = false;
    };
  }, [api, selected]);

  const openBlade = useCallback((s: TimeSession, siblings?: readonly TimeSession[]) => {
    setSelected(s);
    setDayEntries(siblings === undefined || siblings.length === 0 ? [s] : siblings);
    setEditDept(s.departmentId);
    setEditIn(toLocalInputValue(s.clockInAt));
    setEditOut(toLocalInputValue(s.clockOutAt));
    setBladeError(null);
    setConfirmDelete(false);
  }, []);

  /**
   * Move to another entry of the same day WITHOUT closing the dialog.
   *
   * Re-seeds the edit fields from the entry being moved to, and drops any unsaved
   * typing on the one being left. That is deliberate and it is why the arrows do not
   * try to carry a draft across: a half-typed clock-out silently following the reader
   * onto the NEXT entry, and then being saved onto it, is a wrong timestamp on the
   * wrong row — far worse than losing an edit somebody has not committed to yet.
   */
  const pageToEntry = useCallback(
    (delta: number) => {
      if (selected === null || dayEntries.length < 2) {
        return;
      }
      const at = dayEntries.findIndex((e) => e.id === selected.id);
      const next = dayEntries[(at === -1 ? 0 : at) + delta];
      if (next === undefined) {
        return;
      }
      openBlade(next, dayEntries);
    },
    [dayEntries, openBlade, selected]
  );

  const closeBlade = useCallback(() => {
    setSelected(null);
    setDayEntries([]);
    setBladeError(null);
    setConfirmDelete(false);
    /*
     * A change that arrived while this modal was open was HELD rather than applied,
     * so it could not overwrite what somebody was typing. This is the moment to take
     * it: the entry is closed, and what a colleague did is now worth seeing.
     */
    applyPending();
  }, [applyPending]);

  const applyRow = useCallback((updated: TimeSession) => {
    setSessions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    setSelected(updated);
    // The arrows page through this list, so a save has to land in it too — otherwise
    // moving away and back would show the pre-save entry and look like the save was
    // lost.
    setDayEntries((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  }, []);

  const handleSave = useCallback(async () => {
    if (selected === null) {
      return;
    }
    setSaving(true);
    setBladeError(null);
    const patch: PatchSessionInput = {};
    const nextIn = fromLocalInputValue(editIn);
    const nextOut = fromLocalInputValue(editOut);
    if (nextIn !== null) {
      patch.clockInAt = nextIn;
    }
    if (nextOut !== null) {
      patch.clockOutAt = nextOut;
    }
    // Only when CHANGED: sending the same id on every save would mark the row edited
    // (and, under the rule added for #7, drop its approval) for no reason.
    if (editDept !== '' && editDept !== selected.departmentId) {
      patch.departmentId = editDept;
    }
    try {
      const updated = await api.patchSession(selected.id, patch);
      applyRow(updated);
    } catch (err: unknown) {
      setBladeError(toErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }, [api, selected, editIn, editOut, editDept, applyRow]);

  const handleApprove = useCallback(async () => {
    if (selected === null) {
      return;
    }
    setSaving(true);
    setBladeError(null);
    try {
      const updated = await api.approveSession(selected.id);
      applyRow(updated);
    } catch (err: unknown) {
      setBladeError(toErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }, [api, selected, applyRow]);

  /**
   * "This entry is fine" — accept the DURATION flags without approving the hours.
   *
   * 🔥 Michael, 2026-09-30: "We need to add a button that okays away a flag for time
   * entries that seem too short or too long. Right now, if someone approves a time
   * entry that has this flag, the flag doesn't go away and the entry doesn't get
   * posted to NetSuite."
   *
   * Both halves of that were real. A too-short entry carries `needsReview`, and the
   * NetSuite poster SKIPS anything carrying it — so an approved, perfectly genuine
   * short entry sat approved and unpayable, and the only trace was a line in a post
   * result nobody reads. A too-long entry is DERIVED from the span on every read, so
   * there was no way to clear it at all; the badge came back every refresh however
   * many times somebody had looked.
   *
   * IT IS NOT AN APPROVAL and does not become one — the server leaves `approvedAt`
   * alone. Approving still decides whether the hours are paid.
   */
  const handleAckFlags = useCallback(async () => {
    if (selected === null) {
      return;
    }
    setSaving(true);
    setBladeError(null);
    try {
      const updated = await api.acknowledgeSessionFlags(selected.id);
      applyRow(updated);
    } catch (err: unknown) {
      setBladeError(toErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }, [api, selected, applyRow]);

  const handleDelete = useCallback(async () => {
    if (selected === null) {
      return;
    }
    const id = selected.id;
    setDeleting(true);
    setBladeError(null);
    try {
      await api.deleteTimeSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      closeBlade();
    } catch (err: unknown) {
      setBladeError(toErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  }, [api, selected, closeBlade]);

  const openClockOut = useCallback((s: TimeSession) => {
    setClockOutTarget(s);
    // Default to now; the supervisor adjusts to when the person actually left.
    setClockOutValue(toLocalInputValue(new Date().toISOString()));
    setClockOutError(null);
  }, []);

  const closeClockOut = useCallback(() => {
    setClockOutTarget(null);
    setClockOutError(null);
  }, []);

  // Force clock-out: write a clock-out onto an OPEN session via the same
  // authorized edit path (server re-checks canApprove + rejects a clock-out that
  // precedes clock-in). The row becomes a CLOSED manual session — durable through
  // reconstruction (unlike an open-session edit). Updates the list in place; does
  // not open the blade.
  const handleClockOut = useCallback(async () => {
    if (clockOutTarget === null) {
      return;
    }
    const nextOut = fromLocalInputValue(clockOutValue);
    if (nextOut === null) {
      setClockOutError('Enter a valid clock-out time.');
      return;
    }
    setClockOutSaving(true);
    setClockOutError(null);
    try {
      const updated = await api.patchSession(clockOutTarget.id, { clockOutAt: nextOut });
      setSessions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      setClockOutTarget(null);
    } catch (err: unknown) {
      setClockOutError(toErrorMessage(err));
    } finally {
      setClockOutSaving(false);
    }
  }, [api, clockOutTarget, clockOutValue]);

  /**
   * The payroll-run preview (Michael, 2026-09-14). PREVIEW ONLY — the API refuses to
   * commit, so this button cannot change anybody's pay. It reads every paycheck in the
   * batch out of NetSuite, so it is slow by nature; the spinner is not decoration.
   */
  const [payrollRun, setPayrollRun] = useState<PayrollRunPreview | null>(null);
  const [payrollRunOpen, setPayrollRunOpen] = useState(false);
  const [payrollRunning, setPayrollRunning] = useState(false);
  const [payrollRunError, setPayrollRunError] = useState<string | null>(null);

  const handlePayrollRun = useCallback(async () => {
    setPayrollRunning(true);
    setPayrollRunError(null);
    setPayrollRun(null);
    setPayrollRunOpen(true);
    try {
      setPayrollRun(await api.previewPayrollRun());
    } catch (err) {
      setPayrollRunError(toErrorMessage(err));
    } finally {
      setPayrollRunning(false);
    }
  }, [api]);

  const handlePost = useCallback(async () => {
    setPosting(true);
    setPostMessage(null);
    setPostError(null);
    setPostOutcomes(new Map());
    try {
      // Dev-B returns the per-entry TimeBillPostResult array directly; derive the
      // aggregate counts + per-session badges from it.
      const results = await api.postToNetSuite();
      let posted = 0;
      let skipped = 0;
      let failed = 0;
      for (const r of results) {
        if (r.status === 'posted') {
          posted += 1;
        } else if (r.status === 'skipped') {
          skipped += 1;
        } else {
          failed += 1;
        }
      }
      const parts = [`${posted} posted`];
      if (skipped > 0) {
        parts.push(`${skipped} skipped`);
      }
      if (failed > 0) {
        parts.push(`${failed} failed`);
      }
      setPostMessage(`Post to NetSuite complete: ${parts.join(', ')}.`);
      setPostOutcomes(indexPostResults(results));
      reload();
    } catch (err: unknown) {
      setPostError(toErrorMessage(err));
    } finally {
      setPosting(false);
    }
  }, [api, reload]);

  const handleEnlarge = useCallback((punchId: string, role: 'open' | 'close') => {
    setEnlarged({ punchId, role });
  }, []);

  const empLabel = useCallback(
    (id: string): string => employeesById.get(id)?.displayName ?? id,
    [employeesById]
  );
  const deptLabel = useCallback(
    (id: string): string => departmentsById.get(id)?.name ?? id,
    [departmentsById]
  );

  /**
   * EVERY session in the selected pay period — posted and unposted together.
   *
   * There used to be a separate Admin-only History tab holding the posted rows,
   * and this partitioned the set between the two. That split existed to stop an
   * unbounded grid from filling with old posted entries; the pay-period filter
   * now does that job far better, so the tab was removed (Michael, 2026-09-10)
   * and one grid shows the whole week. Posted rows stay frozen on their own
   * merits — `isApprovable` excludes them, and delete is refused for them — so
   * merging the two views grants nobody anything.
   */
  /**
   * Narrow the page to ONE kind of flag (Michael, 2026-09-14: "a summary of all the
   * time entries that have flags").
   *
   * Client-side, unlike the post-result filter, because a flag is DERIVED — three of
   * the six are computed at read time and two of those from other sessions — so there
   * is no column to select on. The summary counts what is loaded and narrows what is
   * shown; it deliberately does not claim to count anything outside the current
   * date/employee filters.
   */
  const [flagFilter, setFlagFilter] = useState<SessionFlagKind | null>(null);

  const visibleSessions = useMemo(() => {
    const rows = activeTab === 'timecards' ? sessions : [];
    if (flagFilter === null) {
      return rows;
    }
    return rows.filter((s) => deriveSessionFlags(s).some((f) => f.kind === flagFilter));
  }, [sessions, activeTab, flagFilter]);

  /** Counts per flag kind across everything loaded, before the narrowing above. */
  const flagCounts = useMemo(() => {
    const counts = new Map<SessionFlagKind, number>();
    for (const session of activeTab === 'timecards' ? sessions : []) {
      for (const flag of deriveSessionFlags(session)) {
        counts.set(flag.kind, (counts.get(flag.kind) ?? 0) + 1);
      }
    }
    return counts;
  }, [sessions, activeTab]);

  // Group the visible rows by employee (contiguous + chronological) and derive
  // per-row layout: employee-group separators + "continuous shift" pairing for
  // same-instant department switches. Pure client-side ordering — no refetch.
  const orderedSessions = useMemo(
    () => orderSessionsByEmployee(visibleSessions, empLabel),
    [visibleSessions, empLabel]
  );
  const layoutMeta = useMemo(
    () => computeRowLayout(orderedSessions, deptLabel, (s) => formatDay(s.clockInAt)),
    [orderedSessions, deptLabel]
  );

  const supervisorsById = useMemo(() => {
    const m = new Map<string, TimeSupervisor>();
    for (const s of supervisors) {
      m.set(s.employeeId, s);
    }
    return m;
  }, [supervisors]);

  // A row is approvable (thus bulk-selectable) when it's closed, not yet approved,
  // and not posted/frozen. Open and already-approved rows offer no checkbox.
  const isApprovable = useCallback(
    (s: TimeSession): boolean =>
      !s.postedToNs &&
      s.clockOutAt !== undefined &&
      !(s.approvedAt !== undefined && s.approvedAt !== ''),
    []
  );

  /**
   * Tick or untick MANY ids in one update — what the calendar's per-card "All" box uses.
   *
   * 🔥 NOT A LOOP OVER `toggleSelect`. A card holding one ticked and two unticked
   * entries would have each of the three FLIPPED, landing on exactly the inverse of
   * "select all". Stating the target value instead makes the card box mean what it says,
   * and does it in one render rather than N.
   */
  const setSelectedMany = useCallback((ids: readonly string[], selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (selected) {
          next.add(id);
        } else {
          next.delete(id);
        }
      }
      return next;
    });
  }, []);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Per-employee blocks with "at a glance" per-department hour totals, split into
  // pending (unapproved) and approved buckets.
  const employeeBlocks = useMemo(
    () => groupSessionsByEmployee(orderedSessions, empLabel, deptLabel),
    [orderedSessions, empLabel, deptLabel]
  );

  /**
   * The seven days of the selected payroll week, Wed→Tue, as `YYYY-MM-DD`.
   *
   * Stepped with UTC arithmetic from a NOON anchor: adding 24h to a local midnight
   * lands on 23:00 the same day when a DST boundary falls inside the week, which
   * would produce a duplicated column and a missing one.
   */
  const weekDays = useMemo(() => {
    if (selectedPeriod === null) {
      return [] as string[];
    }
    const out: string[] = [];
    const cursor = new Date(`${selectedPeriod.startDate}T12:00:00Z`);
    for (let i = 0; i < 7; i += 1) {
      out.push(cursor.toISOString().slice(0, 10));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return out;
  }, [selectedPeriod]);

  /**
   * Employee → supervisor NAME (Michael's #6: "we need to make it easier to tell
   * which supervisor is responsible for an employee").
   *
   * Resolved client-side from data already loaded: `employee.nsSupervisorId` is a
   * NetSuite id, so it is matched against other employees' `nsEmployeeId` rather
   * than their primary key. No extra request, and no new PII — both fields are
   * already on the DTO this page fetches.
   */
  const supervisorNameByEmployee = useMemo(() => {
    const byNsId = new Map<string, string>();
    for (const e of employees) {
      if (e.nsEmployeeId !== null && e.nsEmployeeId !== '') {
        byNsId.set(e.nsEmployeeId, e.displayName);
      }
    }
    const out = new Map<string, string>();
    for (const e of employees) {
      const name = e.nsSupervisorId === null ? undefined : byNsId.get(e.nsSupervisorId);
      if (name !== undefined) {
        out.set(e.id, name);
      }
    }
    return out;
  }, [employees]);

  // Approvable rows currently visible, and the subset the reviewer has selected.
  // Selection actions work off the VISIBLE approvable set, so a stale/hidden id
  // never counts toward the "Approve selected (n)" action.
  const approvableVisible = useMemo(
    () => orderedSessions.filter(isApprovable),
    [orderedSessions, isApprovable]
  );
  const selectedApprovableIds = useMemo(
    () => approvableVisible.filter((s) => selectedIds.has(s.id)).map((s) => s.id),
    [approvableVisible, selectedIds]
  );

  const selectAllUnapproved = useCallback(() => {
    setSelectedIds(new Set(approvableVisible.map((s) => s.id)));
  }, [approvableVisible]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Clear any selection when the surface tab changes.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab]);

  const handleBulkApprove = useCallback(async () => {
    if (selectedApprovableIds.length === 0) {
      return;
    }
    setBulkApproving(true);
    setBulkMessage(null);
    try {
      const outcomes = await api.approveSessions(selectedApprovableIds);
      const ok = outcomes.filter((o) => o.ok).length;
      const failed = outcomes.length - ok;
      setBulkMessage(
        `Approved ${ok} session${ok === 1 ? '' : 's'}${failed > 0 ? `, ${failed} failed` : ''}.`
      );
      setSelectedIds(new Set());
      reload();
    } catch (err: unknown) {
      setBulkMessage(`Bulk approve failed: ${toErrorMessage(err)}`);
    } finally {
      setBulkApproving(false);
    }
  }, [api, selectedApprovableIds, reload]);

  // Blade detail: render one punch's photo affordance (thumbnail when stored /
  // still unknown, "No photo" badge when metadata says missing). Policy is not
  // surfaced to web-app, so a missing photo escalates to the warning treatment.
  const renderPhotoItem = (session: TimeSession, role: 'open' | 'close'): JSX.Element | null => {
    const punchId = role === 'open' ? session.openPunchId : session.closePunchId;
    const meta: PunchPhotoMeta | undefined = photoMeta?.find((m) => m.role === role);
    if (punchId === undefined && meta === undefined) {
      return null;
    }
    const label = role === 'open' ? 'Clock-in' : 'Clock-out';
    const badge = meta !== undefined ? photoBadgeKind(meta.status, undefined) : 'none';
    const showThumb = punchId !== undefined && (meta === undefined || meta.status === 'stored');
    return (
      <div key={role} className={styles.photoItem}>
        <Text size={200}>{label}</Text>
        {showThumb ? (
          <PunchThumbnail punchId={punchId} role={role} onEnlarge={handleEnlarge} />
        ) : null}
        <MissingPhotoBadge kind={badge} reason={meta?.missingReason} />
        {meta?.capturedAt !== undefined ? (
          <Text size={100}>Captured {formatClock(meta.capturedAt)}</Text>
        ) : null}
      </div>
    );
  };

  /**
   * Where the open entry sits in its day's list — the "2" in "Entry 2 of 3".
   *
   * Derived from the SELECTED id rather than held as its own state: a save replaces
   * the session object, and an index stored separately would be one render behind it.
   */
  const entryIndex = useMemo(
    () => (selected === null ? -1 : dayEntries.findIndex((e) => e.id === selected.id)),
    [dayEntries, selected]
  );

  const approveAllowed = canApproveInUi(isAdmin, isViewer, link);
  const showNotLinked = shouldShowNotLinkedBanner(link, isAdmin);
  // The bulk-select column is offered only on the approval queue (timecards tab)
  // and only to a caller who can actually approve.
  const showSelectColumn = activeTab === 'timecards' && approveAllowed;

  // Body columns for the timecard table. Employee is intentionally ABSENT — the
  // name lives in each employee block's at-a-glance header — and selection is a
  // dedicated leading cell rendered by the table, not a column here.
  const columns: ReadonlyArray<DataColumn<TimeSession>> = useMemo(
    () => [
      {
        columnId: 'day',
        header: 'Day',
        // "Sep 09, 2026" is redundant inside a single pay period — the year and
        // the month repeat on every row. The short form is shown on a phone.
        renderCell: (s) => (
          <>
            <span className={styles.wideOnly}>{formatDay(s.clockInAt)}</span>
            <span className={styles.narrowOnly}>{formatDayShort(s.clockInAt)}</span>
          </>
        ),
      },
      {
        columnId: 'department',
        header: 'Department',
        renderCell: (s) => {
          const label = deptLabel(s.departmentId);
          const m = layoutMeta.get(s.id);
          if (m === undefined || m.chainColor === null) {
            return label;
          }
          return (
            <span className={styles.deptCell}>
              {label}
              <Tooltip content={m.linkNote} relationship="label">
                <Badge appearance="tint" color="brand" size="small">
                  ⇄ Continuous
                </Badge>
              </Tooltip>
            </span>
          );
        },
      },
      { columnId: 'in', header: 'In', renderCell: (s) => formatClock(s.clockInAt) },
      {
        columnId: 'out',
        header: 'Out',
        renderCell: (s) => {
          if (s.clockOutAt !== undefined) {
            return formatClock(s.clockOutAt);
          }
          // Open session — offer a force clock-out to an admin / supervisor for
          // someone who forgot to clock out.
          if (approveAllowed && !s.postedToNs) {
            return (
              <span onClick={(e) => e.stopPropagation()} role="presentation">
                <Button size="small" appearance="secondary" onClick={() => openClockOut(s)}>
                  Clock out
                </Button>
              </span>
            );
          }
          return formatClock(s.clockOutAt);
        },
      },
      {
        columnId: 'hours',
        header: 'Hours',
        renderCell: (s) => {
          const h = elapsedHours(s.clockInAt, s.clockOutAt);
          return h === null ? '—' : h.toFixed(2);
        },
      },
      {
        columnId: 'photo',
        header: 'Photo',
        renderCell: (s) => {
          // Photos attach to kiosk punches only; manual sessions show nothing.
          if (!sessionShowsPhoto(s) || s.openPunchId === undefined) {
            return <Text>—</Text>;
          }
          return (
            <span className={styles.photoCell}>
              <PunchThumbnail punchId={s.openPunchId} role="open" onEnlarge={handleEnlarge} />
              {s.closePunchId !== undefined ? (
                <PunchThumbnail punchId={s.closePunchId} role="close" onEnlarge={handleEnlarge} />
              ) : null}
            </span>
          );
        },
      },
      {
        columnId: 'status',
        header: 'Status',
        renderCell: (s) => (
          <>
            <span className={styles.wideOnly}>{statusLabel(s)}</span>
            <span className={styles.narrowOnly}>{shortStatusLabel(s)}</span>
          </>
        ),
      },
      {
        columnId: 'approval',
        header: 'Approval',
        renderCell: (s) => {
          const approved = s.approvedAt !== undefined && s.approvedAt !== '';
          if (approved && s.approvedVia !== undefined) {
            const b = approvedViaBadge(s.approvedVia);
            return (
              <span className={styles.approvalCell}>
                <Tooltip content={b.tooltip} relationship="label">
                  <Badge appearance="tint" color={b.color}>
                    {b.label}
                  </Badge>
                </Tooltip>
              </span>
            );
          }
          if (!approved && needsAdminIds.has(s.employeeId)) {
            return (
              <span className={styles.approvalCell}>
                <Tooltip
                  content="This employee has no usable supervisor — only an admin can approve their time (override)."
                  relationship="label"
                >
                  <Badge appearance="filled" color="warning">
                    Needs admin approval
                  </Badge>
                </Tooltip>
              </span>
            );
          }
          return <Text>—</Text>;
        },
      },
      {
        columnId: 'postResult',
        header: 'Post result',
        renderCell: (s) => {
          /**
           * The PERSISTED outcome is the source of truth; the in-memory map only
           * covers the run this tab just fired.
           *
           * In-memory FIRST, because a post that just finished is newer than the list
           * this page loaded — but the stored value is what survives a reload, and
           * what a supervisor who did not press the button sees at all. Before this
           * was stored, every row read "—" for everyone except the person who posted.
           */
          const outcome =
            postOutcomes.get(s.id) ??
            (s.postStatus === undefined
              ? undefined
              : postOutcomeBadge(s.postStatus, s.postMessage));
          if (outcome === undefined) {
            return <Text>—</Text>;
          }
          return (
            <Tooltip content={outcome.tooltip} relationship="label">
              <Badge appearance="filled" color={outcome.color}>
                {outcome.label}
              </Badge>
            </Tooltip>
          );
        },
      },
      {
        columnId: 'flags',
        header: 'Flags',
        renderCell: (s) => {
          const flags = deriveSessionFlags(s);
          if (flags.length === 0) {
            return <Text>—</Text>;
          }
          return (
            <span className={styles.flagCell}>
              {flags.map((f) => (
                <Tooltip key={f.kind} content={f.description} relationship="label">
                  <Badge appearance="filled" color={badgeColor(f.severity)}>
                    {f.label}
                  </Badge>
                </Tooltip>
              ))}
            </span>
          );
        },
      },
    ],
    [
      deptLabel,
      layoutMeta,
      approveAllowed,
      openClockOut,
      styles.flagCell,
      styles.photoCell,
      styles.approvalCell,
      styles.deptCell,
      handleEnlarge,
      postOutcomes,
      needsAdminIds,
    ]
  );

  const flaggedCount = visibleSessions.filter(hasAnyFlag).length;
  const totalCols = (showSelectColumn ? 1 : 0) + columns.length;
  const chainClasses = [styles.chain0, styles.chain1, styles.chain2, styles.chain3];

  /**
   * Phone: one card per shift.
   *
   * 🔥 RENDERED IN BOTH VIEW MODES, which is the whole reason it is a variable. It
   * used to live inside the list branch, so when the calendar replaced the list it
   * would have taken the phone layout with it -- and the calendar is a seven-column
   * grid that cannot render below 640px (each day would be ~30px wide). That failure
   * is silent: the grid does not error, it just becomes unreadable. `mobileBlocks` is
   * display:none above the breakpoint and the calendar is display:none below it, so
   * exactly one of the two is ever on screen.
   */
  const mobileShiftCards = (
    <div className={styles.mobileBlocks}>
      {employeeBlocks.map((block) => (
        <div key={block.employeeId} className={styles.mBlock}>
          <span className={styles.mBlockName}>{block.employeeName}</span>
          <span className={styles.mBlockTotals}>
            {block.deptTotals.length === 0 ? (
              'No hours yet'
            ) : (
              <>
                <span className={styles.glancePending}>
                  {block.totalUnapproved.toFixed(2)}h unapproved
                </span>
                {' · '}
                <span className={styles.glanceDone}>
                  {block.totalApproved.toFixed(2)}h approved
                </span>
              </>
            )}
          </span>
          {block.sessions.map((s) => (
            <div
              key={s.id}
              className={styles.mCard}
              onClick={() => openBlade(s)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openBlade(s);
                }
              }}
            >
              {showSelectColumn && isApprovable(s) ? (
                <span
                  className={styles.selectCell}
                  onClick={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  <Checkbox
                    checked={selectedIds.has(s.id)}
                    onChange={() => toggleSelect(s.id)}
                    label="Select for approval"
                  />
                </span>
              ) : null}
              {columns.map((col) => (
                <span key={col.columnId} className={styles.mField}>
                  <span className={styles.mLabel}>{col.header}</span>
                  <span className={styles.mValue}>{col.renderCell(s)}</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <Title3>Hours &amp; Timesheet</Title3>
          <div className={styles.subtitle}>
            {activeTab !== 'reports' ? (
              <>
                <Body1>
                  {visibleSessions.length} session{visibleSessions.length === 1 ? '' : 's'}
                  {flaggedCount > 0 ? ` · ${flaggedCount} flagged for review` : ''}
                </Body1>
                {/*
                  THE FLAG SUMMARY. One chip per kind that actually occurs, so a clean
                  page shows nothing rather than a row of zeroes. Clicking narrows to
                  that kind; clicking the active one clears it, so the summary is also
                  the way back out.
                */}
                {flagCounts.size > 0 ? (
                  <div className={styles.flagSummary}>
                    {FLAG_SUMMARY_ORDER.filter((kind) => (flagCounts.get(kind) ?? 0) > 0).map(
                      (kind) => (
                        <Button
                          key={kind}
                          size="small"
                          appearance={flagFilter === kind ? 'primary' : 'outline'}
                          onClick={() => setFlagFilter((f) => (f === kind ? null : kind))}
                        >
                          {`${FLAG_SUMMARY_LABELS[kind]} · ${String(flagCounts.get(kind) ?? 0)}`}
                        </Button>
                      )
                    )}
                    {flagFilter !== null ? (
                      <Button size="small" appearance="transparent" onClick={() => setFlagFilter(null)}>
                        Clear
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
        {activeTab === 'timecards' ? (
          /*
           * List / Calendar. Visible to EVERY role that can see the timecard, not
           * just admins: the calendar is a reading aid, and a supervisor checking
           * their own team is exactly who benefits from seeing a missed clock-out
           * as a bar crossing three days.
           */
          <div className={styles.bulkBar}>
            <Button
              appearance={viewMode === 'list' ? 'primary' : 'secondary'}
              onClick={() => {
                setViewMode('list');
              }}
            >
              List
            </Button>
            <Button
              appearance={viewMode === 'calendar' ? 'primary' : 'secondary'}
              onClick={() => {
                setViewMode('calendar');
              }}
            >
              Calendar
            </Button>
          </div>
        ) : null}
        {activeTab === 'timecards' && isAdmin ? (
          <div className={styles.bulkBar}>
            {/* "New time entry" used to sit here in the top-right corner, away from
                the row actions it belongs with (Michael, 2026-09-10: counter-
                intuitive). It now lives beside "Select all unapproved" below the
                filters. Posting to NetSuite stays here — it is a page-level admin
                action, not a per-row one. */}
            <div className={styles.adminActions}>
              <Button appearance="primary" onClick={() => void handlePost()} disabled={posting}>
                {posting ? <Spinner size="tiny" /> : 'Post approved to NetSuite'}
              </Button>
              {/* Secondary, not primary: it is the cautious one of the pair — it only
                  ever shows you what WOULD change. */}
              <Button onClick={() => void handlePayrollRun()} disabled={payrollRunning}>
                {payrollRunning ? <Spinner size="tiny" /> : 'Review payroll corrections'}
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <NewTimeEntryDialog open={newEntryOpen} onOpenChange={setNewEntryOpen} onCreated={reload} />

      {showNotLinked ? (
        <div className={styles.warningBanner} role="status">
          <Text>{NOT_LINKED_BANNER}</Text>
        </div>
      ) : null}

      {/* Above the tabs, so it is visible from every surface — somebody sitting on the
          clock overnight is not a fact about the tab you happen to have open. `alert`
          rather than `status` because it wants to interrupt. */}
      {/*
        The headline alone, with the names behind a toggle (Michael, 2026-09-10).
        Spelling out six names with two decimal places each, plus a paragraph
        explaining what the threshold means, filled an entire phone screen before any
        timecard was reachable. The COUNT is the alert; the names are the detail
        you open when you act on it.
      */}
      {openLongShifts.length > 0 ? (
        <MessageBar intent="warning" role="alert">
          <MessageBarBody>
            <MessageBarTitle>
              {openLongShifts.length === 1
                ? `1 person clocked in over ${String(LONG_SHIFT_THRESHOLD_HOURS)} hours`
                : `${String(openLongShifts.length)} people clocked in over ` +
                  `${String(LONG_SHIFT_THRESHOLD_HOURS)} hours`}
            </MessageBarTitle>{' '}
            <Button
              appearance="transparent"
              size="small"
              onClick={() => setLongShiftsOpen((v) => !v)}
              aria-expanded={longShiftsOpen}
            >
              {longShiftsOpen ? 'Hide names' : 'Show names'}
            </Button>
            {longShiftsOpen ? (
              <div className={styles.longShiftNames}>
                {/*
                  EACH NAME OPENS THAT ENTRY (Michael, 2026-09-14: "having each
                  instance be a link to that specific time entry so supers and admin
                  can just click that and be lead directly to the time entry to see
                  what's up").

                  It opens the BLADE rather than scrolling to the row, because the row
                  is usually not on screen to scroll to: this banner queries every
                  date, deliberately, and the offender is typically YESTERDAY'S
                  clock-in while the supervisor is looking at today. Scrolling would
                  have found nothing and looked broken.
                */}
                {openLongShifts.map((s) => (
                  <div key={s.id}>
                    <Button
                      appearance="transparent"
                      size="small"
                      className={styles.longShiftLink}
                      onClick={() => openBlade(s)}
                    >
                      {empLabel(s.employeeId)} — {String(s.longShift?.hours ?? 0)} h
                    </Button>
                  </div>
                ))}
                <Text className={styles.longShiftNote}>
                  These are most likely missed clock-outs.
                </Text>
              </div>
            ) : null}
          </MessageBarBody>
        </MessageBar>
      ) : null}

      <div className={styles.tabStrip}>
        <TabList
          selectedValue={activeTab}
          onTabSelect={(_, d) => setActiveTab(d.value as TimecardTab)}
          aria-label="Timesheet surface"
        >
          <Tab value="timecards">Employee Timecards</Tab>
          <Tab value="reports">My Reports</Tab>
        </TabList>
      </div>

      {activeTab === 'reports' ? (
        <MyReportsTab />
      ) : (
        <>
          {postMessage !== null ? (
            <div className={styles.banner} role="status">
              <Text>{postMessage}</Text>
            </div>
          ) : null}
          {postError !== null ? (
            <div className={styles.banner} role="alert">
              <Text className={styles.error}>{postError}</Text>
            </div>
          ) : null}

          <div className={styles.filters}>
            <Field label="Pay period">
              <Dropdown
                value={
                  selectedPeriod === null ? 'All dates' : payrollPeriodOptionLabel(periodOffset ?? 0, selectedPeriod)
                }
                selectedOptions={[periodOffset === null ? 'all' : String(periodOffset)]}
                onOptionSelect={(_, d) =>
                  setPeriodOffset(d.optionValue === 'all' ? null : Number(d.optionValue))
                }
              >
                {periodChoices.map((offset) => {
                  const p = shiftPayrollPeriod(currentPeriod, offset);
                  const label = payrollPeriodOptionLabel(offset, p);
                  return (
                    <Option key={offset} value={String(offset)} text={label}>
                      {label}
                    </Option>
                  );
                })}
                <Option value="all" text="All dates">
                  All dates
                </Option>
              </Dropdown>
            </Field>
            <Field label="Department">
              <Dropdown
                value={
                  filters.departmentId === ALL
                    ? 'All departments'
                    : deptLabel(filters.departmentId ?? ALL)
                }
                selectedOptions={[filters.departmentId ?? ALL]}
                onOptionSelect={(_, d) =>
                  setFilters((f) => ({ ...f, departmentId: d.optionValue ?? ALL }))
                }
              >
                <Option value={ALL} text="All departments">
                  All departments
                </Option>
                {departments.map((d) => (
                  <Option key={d.id} value={d.id} text={d.name}>
                    {d.name}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            {/*
              * Michael, 2026-09-24: "We're getting confused having to scroll through the
              * entire list of employees every time we want to make sure all time entries
              * have been caught."
              *
              * A CHECKBOX RATHER THAN ANOTHER DROPDOWN, because during payroll this is
              * not one of several statuses somebody browses — it is the single question
              * being asked over and over ("what is left?"), and it wants one click on and
              * one click off.
              *
              * It narrows SERVER-SIDE via the existing `unapproved` status, so employees
              * whose week is fully approved drop out of the list entirely rather than
              * being rendered and scrolled past. That is the point: an empty screen means
              * finished.
              */}
            <Field label="Show">
              <Checkbox
                label="Only unapproved"
                checked={filters.status === 'unapproved'}
                onChange={(_, d) => {
                  setFilters((f) => {
                    // exactOptionalPropertyTypes: drop the key to clear it rather than
                    // assigning undefined.
                    const { status: _drop, ...rest } = f;
                    return d.checked === true ? { ...rest, status: 'unapproved' } : rest;
                  });
                }}
              />
            </Field>
            <Field label="Employee">
              <Dropdown
                value={
                  filters.employeeId === ALL ? 'All employees' : empLabel(filters.employeeId ?? ALL)
                }
                selectedOptions={[filters.employeeId ?? ALL]}
                onOptionSelect={(_, d) =>
                  setFilters((f) => ({ ...f, employeeId: d.optionValue ?? ALL }))
                }
              >
                <Option value={ALL} text="All employees">
                  All employees
                </Option>
                {employees.map((e) => (
                  <Option key={e.id} value={e.id} text={e.displayName}>
                    {e.displayName}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            {isAdmin ? (
              <Field label="Supervisor">
                <Dropdown
                  value={
                    filters.supervisorId === undefined || filters.supervisorId === ALL
                      ? 'All supervisors'
                      : (supervisorsById.get(filters.supervisorId)?.displayName ??
                        filters.supervisorId)
                  }
                  selectedOptions={[filters.supervisorId ?? ALL]}
                  onOptionSelect={(_, d) =>
                    setFilters((f) => {
                      // exactOptionalPropertyTypes: omit the key entirely to clear it,
                      // rather than assigning `undefined`.
                      const { supervisorId: _drop, ...rest } = f;
                      return d.optionValue === undefined || d.optionValue === ALL
                        ? rest
                        : { ...rest, supervisorId: d.optionValue };
                    })
                  }
                >
                  <Option value={ALL} text="All supervisors">
                    All supervisors
                  </Option>
                  {supervisors.map((sup) => (
                    <Option key={sup.employeeId} value={sup.employeeId} text={sup.displayName}>
                      {sup.displayName}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            ) : null}

            {/*
              POST RESULT — its own dimension, not another value on the status filter,
              so "unapproved AND failed to post" is askable. Michael, 2026-09-14: "so we
              don't have to scroll through the entire page to find specific time
              entries."

              "Not attempted" is spelled out rather than left as a blank: a session
              nobody has tried to post and one that was tried and skipped are different
              problems, and only one of them means somebody has work to do.
            */}
            <Field label="Post result">
              <Dropdown
                value={POST_RESULT_LABELS[filters.postResult ?? ALL]}
                selectedOptions={[filters.postResult ?? ALL]}
                onOptionSelect={(_, d) =>
                  setFilters((f) => {
                    // exactOptionalPropertyTypes: omit the key to clear it.
                    const { postResult: _drop, ...rest } = f;
                    return d.optionValue === undefined || d.optionValue === ALL
                      ? rest
                      : { ...rest, postResult: d.optionValue as PostResultFilter };
                  })
                }
              >
                {(Object.keys(POST_RESULT_LABELS) as (keyof typeof POST_RESULT_LABELS)[]).map(
                  (key) => (
                    <Option key={key} value={key} text={POST_RESULT_LABELS[key]}>
                      {POST_RESULT_LABELS[key]}
                    </Option>
                  )
                )}
              </Dropdown>
            </Field>
          </div>

          {/*
            The bar renders when EITHER part applies, not just when there are rows to
            approve — gating the whole bar on `approvableVisible.length > 0` (as it was
            when it held only the bulk actions) would hide "New time entry" on a day
            with nothing left unapproved, which is exactly when you need to add a
            missed shift.
          */}
          {!isViewer || (showSelectColumn && approvableVisible.length > 0) ? (
            <div className={styles.bulkBar}>
              {/* A View-Only user must not create payable hours; the API refuses them
                  regardless, this just keeps the affordance honest. */}
              {!isViewer ? (
                <Button appearance="secondary" onClick={() => setNewEntryOpen(true)}>
                  New time entry
                </Button>
              ) : null}
              {showSelectColumn && approvableVisible.length > 0 ? (
                <>
                  <Button
                    appearance="secondary"
                    onClick={selectAllUnapproved}
                    disabled={
                      bulkApproving || approvableVisible.length === selectedApprovableIds.length
                    }
                  >
                    Select all unapproved
                  </Button>
                  {selectedApprovableIds.length > 0 ? (
                    <Button appearance="subtle" onClick={clearSelection} disabled={bulkApproving}>
                      Clear
                    </Button>
                  ) : null}
                  <Button
                    appearance="primary"
                    onClick={() => void handleBulkApprove()}
                    disabled={selectedApprovableIds.length === 0 || bulkApproving}
                  >
                    {bulkApproving ? (
                      <Spinner size="tiny" />
                    ) : (
                      `Approve selected (${selectedApprovableIds.length})`
                    )}
                  </Button>
                </>
              ) : null}
            </div>
          ) : null}
          {bulkMessage !== null ? (
            <div className={styles.banner} role="status">
              <Text>{bulkMessage}</Text>
            </div>
          ) : null}

          {error !== null ? (
            <Text className={styles.error} role="alert">
              {error}
            </Text>
          ) : null}

          {loading ? (
            <Spinner label="Loading sessions…" />
          ) : employeeBlocks.length === 0 ? (
            <div className={styles.emptyCell}>
              <Text>
                {selectedPeriod === null
                  ? 'No time sessions match the current filters.'
                  : `No time sessions in ${selectedPeriod.startDate} – ${selectedPeriod.endDate}. Pick another pay period to look further back.`}
              </Text>
            </div>
          ) : viewMode === 'calendar' ? (
            <>
            {mobileShiftCards}
            {/*
              * The week as a calendar. Bars are positioned and sized by the time they
              * actually occupy, and one that crosses a day boundary is drawn crossing
              * the columns in alarm colours — the missed clock-out this view exists to
              * make obvious rather than something to go and find in a list.
              *
              * 🔥 THE BRACES ARE LOAD-BEARING. This comment sat in EXPRESSION position
              * while the branch was a bare parenthesised element, so it was an ordinary
              * JS comment. Wrapping the branch in a fragment made it a JSX CHILD, and a
              * bare block comment as a JSX child is literal TEXT -- it rendered on the
              * page. It type-checks, it lints, and the build says nothing; the only
              * tell is the comment surviving minification as a string in the bundle.
              */}
            <WeekCalendar
              blocks={employeeBlocks}
              days={weekDays}
              supervisorNameByEmployee={supervisorNameByEmployee}
              departmentNameById={departmentNameById}
              presenceBySession={presenceBySession}
              /*
               * The card hands over EVERY entry of that day, so the dialog can page
               * between them with its arrows. Opening from the list passes nothing and
               * the arrows stay hidden.
               */
              onSelect={(session, daySessions) => {
                openBlade(session, daySessions);
              }}
              /*
               * Per-ENTRY selection, back on the cards (Michael, 2026-09-30: "I need you
               * to put in a checkbox next to each time entry on a card so that people can
               * just go through and check the entries one by one").
               *
               * Same selection set the list uses, so "Approve selected (n)" in the toolbar
               * counts calendar ticks without knowing which view made them.
               */
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onSetSelected={setSelectedMany}
              {...(approveAllowed ? { isApprovable } : {})}
            />
            </>
          ) : (
            <>
            {mobileShiftCards}
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {showSelectColumn ? (
                      <th className={styles.headCell} aria-label="Select" />
                    ) : null}
                    {columns.map((col) => (
                      <th
                        key={col.columnId}
                        className={mergeClasses(
                          styles.headCell,
                          MOBILE_HIDDEN_COLUMNS.has(col.columnId) ? styles.hideOnMobile : undefined
                        )}
                      >
                        {col.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                {employeeBlocks.map((block) => (
                  <tbody key={block.employeeId}>
                    <tr>
                      <td className={styles.glanceCell} colSpan={totalCols}>
                        <div className={styles.glanceHeader}>
                          <span className={styles.glanceName}>{block.employeeName}</span>
                          {block.deptTotals.length > 0 ? (
                            <div className={styles.glanceTotalsRow}>
                              {block.deptTotals.map((t) => (
                                <span
                                  key={t.departmentId}
                                  className={mergeClasses(
                                    styles.glanceDeptGroup,
                                    styles.glanceDeptOnMobile
                                  )}
                                >
                                  <span className={styles.glanceDeptName}>{t.departmentName}</span>
                                  {t.unapprovedHours > 0 ? (
                                    <span className={styles.glancePending}>
                                      {t.unapprovedHours.toFixed(2)}h unapproved
                                    </span>
                                  ) : null}
                                  {t.unapprovedHours > 0 && t.approvedHours > 0 ? (
                                    <span className={styles.glanceSep}>·</span>
                                  ) : null}
                                  {t.approvedHours > 0 ? (
                                    <span className={styles.glanceDone}>
                                      {t.approvedHours.toFixed(2)}h approved
                                    </span>
                                  ) : null}
                                </span>
                              ))}
                              <span className={styles.glanceTotalGroup}>
                                <span className={styles.glanceTotalLabel}>Total</span>
                                <span className={styles.glancePending}>
                                  {block.totalUnapproved.toFixed(2)}h unapproved
                                </span>
                                <span className={styles.glanceSep}>·</span>
                                <span className={styles.glanceDone}>
                                  {block.totalApproved.toFixed(2)}h approved
                                </span>
                              </span>
                            </div>
                          ) : (
                            <span className={styles.glanceMuted}>No hours yet</span>
                          )}
                        </div>
                      </td>
                    </tr>
                    {block.sessions.map((s) => {
                      const m = layoutMeta.get(s.id);
                      const bandClass = m?.dateBand === 1 ? styles.bandB : styles.bandA;
                      const chainClass =
                        m != null && m.chainColor !== null ? chainClasses[m.chainColor] : undefined;
                      return (
                        <Fragment key={s.id}>
                          {m?.gapBefore ? (
                            <tr aria-hidden="true">
                              <td className={styles.gapCell} colSpan={totalCols} />
                            </tr>
                          ) : null}
                          <tr
                            className={mergeClasses(styles.dataRow, bandClass, chainClass)}
                            onClick={() => openBlade(s)}
                          >
                            {showSelectColumn ? (
                              <td className={styles.cell}>
                                {isApprovable(s) ? (
                                  <span
                                    className={styles.selectCell}
                                    onClick={(e) => e.stopPropagation()}
                                    role="presentation"
                                  >
                                    <Checkbox
                                      checked={selectedIds.has(s.id)}
                                      onChange={() => toggleSelect(s.id)}
                                      aria-label="Select session for approval"
                                    />
                                  </span>
                                ) : null}
                              </td>
                            ) : null}
                            {columns.map((col) => (
                              <td
                                key={col.columnId}
                                className={mergeClasses(
                                  styles.cell,
                                  MOBILE_HIDDEN_COLUMNS.has(col.columnId)
                                    ? styles.hideOnMobile
                                    : undefined
                                )}
                              >
                                {col.renderCell(s)}
                              </td>
                            ))}
                          </tr>
                        </Fragment>
                      );
                    })}
                  </tbody>
                ))}
              </table>
            </div>
            </>
          )}
        </>
      )}

      <CenterModal
        open={selected !== null}
        onClose={closeBlade}
        title={
          selected === null
            ? ''
            : `${empLabel(selected.employeeId)} — ${formatDay(selected.clockInAt)}`
        }
      >
        {selected !== null ? (
          <div className={styles.form}>
            {/*
              * 🔥 THE INDICATOR, where it actually matters (Michael, 2026-09-19: "I
              * don't want two people working on the same thing"). It sits ABOVE the
              * fields rather than beside the row in the grid, because this is the
              * moment a second person is about to start typing over somebody else's
              * work — a badge they passed on the way in is one they have stopped
              * reading.
              *
              * ADVISORY: it does not disable anything. Both people can still save, and
              * the last save wins, exactly as before. What changes is that the second
              * person KNOWS, and can go and ask.
              */}
            {presenceBySession.has(selected.id) ? (
              <MessageBar intent="warning">
                <MessageBarBody>{presenceBySession.get(selected.id)}</MessageBarBody>
              </MessageBar>
            ) : null}
            {/*
              * Something changed elsewhere while this was open. It is NOT applied yet
              * — that would replace the row mid-edit — so the entry says so and takes
              * it on close.
              */}
            {pendingChange ? (
              <MessageBar intent="info">
                <MessageBarBody>
                  This timesheet changed somewhere else. Your screen updates when you
                  close this entry.
                </MessageBarBody>
              </MessageBar>
            ) : null}
            {/*
              * THE ENTRY PAGER — "Entry 2 of 3 · Approved" with an arrow either side.
              *
              * 🔥 Michael, 2026-09-30: "I want to add arrows though that can switch back
              * and forth between the entries and update the header status to show
              * 'Entry 1', 'Entry 2', etc... This is also how we know which entry each
              * flag is for."
              *
              * It is the OTHER HALF of collapsing a day onto one card. The card now
              * says "this day has three stretches and one of them is flagged"; without
              * this the reviewer would have to close the dialog and work out which
              * stretch by its clock times. The card's flag tooltips name the entry by
              * the same number shown here, so the tooltip and the arrows are one
              * instruction.
              *
              * The row renders even for a single entry, holding the status it always
              * held — an element that appears only sometimes makes the dialog jump
              * around as somebody pages through a day.
              */}
            <div className={styles.entryPager}>
              {dayEntries.length > 1 ? (
                <Button
                  appearance="subtle"
                  size="small"
                  aria-label="Previous entry on this day"
                  disabled={saving || entryIndex <= 0}
                  onClick={() => {
                    pageToEntry(-1);
                  }}
                >
                  {'←'}
                </Button>
              ) : null}
              <Text weight="semibold">
                {dayEntries.length > 1
                  ? `Entry ${String(entryIndex + 1)} of ${String(dayEntries.length)} · ${statusLabel(selected)}`
                  : statusLabel(selected)}
              </Text>
              {dayEntries.length > 1 ? (
                <Button
                  appearance="subtle"
                  size="small"
                  aria-label="Next entry on this day"
                  disabled={saving || entryIndex >= dayEntries.length - 1}
                  onClick={() => {
                    pageToEntry(1);
                  }}
                >
                  {'→'}
                </Button>
              ) : null}
            </div>

            {/*
              * Department is EDITABLE for anyone who can approve this entry — admins
              * AND the employee's own supervisor (Michael, 2026-09-30: "Supervisors
              * need to be able to change departments for entries like admin can").
              *
              * 🔥 IT IS NOT COSMETIC, and that is why it was admin-only until now: a
              * department change moves the hours to a different earning type on the
              * paycheck, or leaves them resolving no earning at all and silently
              * unposted. What that reasoning got wrong is WHO can judge it. The cause
              * is somebody clocking in at the wrong kiosk, and the person who knows
              * which department the shift was actually worked in is the supervisor who
              * was standing there.
              *
              * `approveAllowed` is the same predicate that gates Approve and Delete, so
              * View Only never gets it and an unlinked manager never gets it. The server
              * re-checks the identical boundary — this is a mirror, not the gate.
              *
              * A posted session is frozen, so the picker is disabled rather than
              * hidden — the value still needs reading on a row somebody is checking.
              */}
            {approveAllowed ? (
              <Field label="Department">
                <Dropdown
                  value={deptLabel(editDept === '' ? selected.departmentId : editDept)}
                  selectedOptions={[editDept === '' ? selected.departmentId : editDept]}
                  disabled={saving || selected.postedToNs || isViewer}
                  onOptionSelect={(_, d) => {
                    setEditDept(d.optionValue ?? selected.departmentId);
                  }}
                >
                  {departments.map((d) => (
                    <Option key={d.id} value={d.id} text={d.name}>
                      {d.name}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            ) : (
              <Text>{deptLabel(selected.departmentId)}</Text>
            )}

            {deriveSessionFlags(selected).length > 0 ? (
              <div className={styles.flagCell}>
                {deriveSessionFlags(selected).map((f) => (
                  <Tooltip key={f.kind} content={f.description} relationship="label">
                    <Badge appearance="filled" color={badgeColor(f.severity)}>
                      {f.label}
                    </Badge>
                  </Tooltip>
                ))}
              </div>
            ) : null}

            {/*
              * Somebody has already accepted this entry's duration. Said plainly, because
              * the absence of a flag is indistinguishable from a flag that never fired,
              * and those mean opposite things on a timesheet somebody is auditing.
              */}
            {selected.flagAckAt !== undefined ? (
              <Text className={styles.ackNote}>
                {`Duration accepted ${formatDay(selected.flagAckAt)} — this entry can post.`}
              </Text>
            ) : null}

            {sessionShowsPhoto(selected) ? (
              <div className={styles.photoRow}>
                {renderPhotoItem(selected, 'open')}
                {renderPhotoItem(selected, 'close')}
              </div>
            ) : null}

            <DateTimeField
              label="Clock in"
              value={editIn}
              onChange={setEditIn}
              disabled={saving || selected.postedToNs || isViewer}
            />
            <DateTimeField
              label="Clock out"
              value={editOut}
              onChange={setEditOut}
              // An open shift has no clock-out; default its day to the day the shift
              // STARTED rather than leaving the date blank for someone to guess at.
              defaultDate={toLocalInputValue(selected.clockInAt).slice(0, 10)}
              disabled={saving || selected.postedToNs || isViewer}
            />

            {selected.postedToNs ? (
              <Text>This session is posted to NetSuite and is frozen. It cannot be edited.</Text>
            ) : null}

            {bladeError !== null ? (
              <Text className={styles.error} role="alert">
                {bladeError}
              </Text>
            ) : null}

            {isViewer ? (
              <Text>View-only access — you can review time but not edit or approve.</Text>
            ) : (
              <div className={styles.actions}>
                <Button
                  appearance="primary"
                  onClick={() => void handleSave()}
                  disabled={saving || selected.postedToNs || isViewer}
                >
                  {saving ? <Spinner size="tiny" /> : 'Save'}
                </Button>
                {approveAllowed ? (
                  <Button
                    appearance="secondary"
                    onClick={() => void handleApprove()}
                    disabled={
                      saving ||
                      selected.postedToNs ||
                      (selected.approvedAt !== undefined && selected.approvedAt !== '')
                    }
                  >
                    Approve
                  </Button>
                ) : (
                  <Tooltip
                    content="You can't approve this session — you're not linked to an employee record, so you have no reports. Contact an admin."
                    relationship="label"
                  >
                    <Button appearance="secondary" disabled>
                      Approve
                    </Button>
                  </Tooltip>
                )}
                {/*
                  * "This entry is fine" — the flag button (Michael, 2026-09-30).
                  *
                  * 🔥 SHOWN ONLY WHEN THERE IS SOMETHING TO ACCEPT. A button that is
                  * always there, mostly doing nothing, is one people press without
                  * reading — and this one releases a post block. It appears when the
                  * entry actually carries a duration flag and disappears the moment it
                  * has been used.
                  *
                  * `needsReview` is the too-SHORT flag (it is what the kiosk rebuild
                  * sets under MIN_PLAUSIBLE_SESSION_MINUTES, and what the poster skips
                  * on); `longShift` is the too-LONG one. Nothing else is acceptable this
                  * way — a missing earning type or an overlapping entry is a real fault
                  * with a real fix, and offering to wave it through would be offering to
                  * pay somebody wrong.
                  */}
                {approveAllowed &&
                !selected.postedToNs &&
                selected.flagAckAt === undefined &&
                (selected.needsReview === true || selected.longShift !== undefined) ? (
                  <Tooltip
                    content="Marks this entry's length as checked and correct, so it stops being flagged and can post. It does NOT approve the hours."
                    relationship="label"
                  >
                    <Button
                      appearance="secondary"
                      onClick={() => void handleAckFlags()}
                      disabled={saving || selected.clockOutAt === undefined}
                    >
                      This entry is fine
                    </Button>
                  </Tooltip>
                ) : null}
                {/*
                  Delete is offered to APPROVERS, not just admins (Michael,
                  2026-09-09) — admin could not keep up with the volume of bad
                  entries. Same predicate as Approve on purpose: a linked Manager
                  sees only their own reports, View-Only never gets it, and the
                  server re-checks the identical boundary. Posted rows stay frozen.
                */}
                {approveAllowed && !selected.postedToNs ? (
                  confirmDelete ? (
                    <Button
                      appearance="secondary"
                      onClick={() => void handleDelete()}
                      disabled={deleting}
                    >
                      {deleting ? <Spinner size="tiny" /> : 'Confirm delete'}
                    </Button>
                  ) : (
                    <Button
                      appearance="secondary"
                      onClick={() => setConfirmDelete(true)}
                      disabled={saving || deleting}
                    >
                      Delete entry
                    </Button>
                  )
                ) : null}
              </div>
            )}
          </div>
        ) : null}
      </CenterModal>

      {/**
       * The payroll-run preview.
       *
       * The WARNINGS go first and are not collapsible. Every one of them is something
       * that cannot be undone or that leaves work for a human in NetSuite, and a
       * reader who skims to the numbers and misses them is the failure this screen has
       * to prevent.
       */}
      <Dialog open={payrollRunOpen} onOpenChange={(_, d) => setPayrollRunOpen(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Payroll corrections — preview</DialogTitle>
            <DialogContent>
              {payrollRunning ? (
                <Text>
                  Reading every paycheck in the batch from NetSuite. This takes a while.
                </Text>
              ) : payrollRunError !== null ? (
                <Text className={styles.error}>{payrollRunError}</Text>
              ) : payrollRun === null ? null : (
                <div className={styles.adminActions}>
                  {payrollRun.warnings.map((w) => (
                    <Text key={w} className={styles.warningText}>
                      {w}
                    </Text>
                  ))}

                  <Text weight="semibold">
                    {`Batch ${payrollRun.batch.batchnumber} · ${String(payrollRun.batch.paycheckCount)} paycheck(s)`}
                    {payrollRun.batch.periodEnding === null
                      ? ''
                      : ` · period ending ${payrollRun.batch.periodEnding.slice(0, 10)}`}
                  </Text>

                  <Text>
                    {`${String(payrollRun.totals.paychecksWithDepartmentWork)} paycheck(s) need department corrections `}
                    {`(${String(payrollRun.totals.hoursMoved)} hours), `}
                    {`${String(payrollRun.totals.paychecksWithSalarySplit)} salaried split(s), `}
                    {`${String(payrollRun.totals.amountRowsToPost)} bonus/reimbursement/commission row(s) to post.`}
                  </Text>

                  {payrollRun.paychecks
                    .filter((p) => p.departments !== null || p.salarySplit !== null || p.problem !== undefined)
                    .map((p) => (
                      <Text key={p.paycheckId}>
                        {`• ${p.employee ?? p.paycheckId}`}
                        {p.departments === null
                          ? ''
                          : ` — ${String(p.departments.hoursMoved)}h across ${String(p.departments.unapplyCount)} line(s)`}
                        {p.salarySplit === null
                          ? ''
                          : ` — split ${String(p.salarySplit.lineCount)} way(s)${p.salarySplit.conserves ? '' : ' ⚠️ DOES NOT CONSERVE GROSS'}`}
                        {p.problem === undefined ? '' : ` — ${p.problem}`}
                      </Text>
                    ))}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setPayrollRunOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <PhotoEnlargeDialog
        punchId={enlarged?.punchId ?? null}
        role={enlarged?.role ?? null}
        onClose={() => setEnlarged(null)}
      />

      <Dialog
        open={clockOutTarget !== null}
        onOpenChange={(_, d) => {
          if (!d.open) {
            closeClockOut();
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Clock out</DialogTitle>
            <DialogContent className={styles.form}>
              {clockOutTarget !== null ? (
                <Text>
                  {empLabel(clockOutTarget.employeeId)} — {deptLabel(clockOutTarget.departmentId)},
                  clocked in {formatClock(clockOutTarget.clockInAt)} on{' '}
                  {formatDay(clockOutTarget.clockInAt)}. Set the time they actually left.
                </Text>
              ) : null}
              <Field label="Clock-out time">
                <Input
                  type="datetime-local"
                  value={clockOutValue}
                  onChange={(_, d) => setClockOutValue(d.value)}
                  disabled={clockOutSaving}
                />
              </Field>
              {clockOutError !== null ? (
                <Text className={styles.error} role="alert">
                  {clockOutError}
                </Text>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={closeClockOut} disabled={clockOutSaving}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={() => void handleClockOut()}
                disabled={clockOutSaving}
              >
                {clockOutSaving ? <Spinner size="tiny" /> : 'Clock out'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
